import ShieldCheckIcon from "@components/Icons/ShieldCheckIcon";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import { ComponentHeader } from "@components/Tables/ComponentHeader";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { LicenseInformationModal, SecurityInformationModal, LicenseConflictModal, PolicyConflictModal, ComponentInformationModal, VexSecuritiesBadge } from "@components/UI";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import {
  FLAG_OPTIONS,
  OBLIGATION_TYPE_OPTIONS,
  VULNERABILITY_CODE_COLOR,
  VUlNERABILITY_TYPE_OPTIONS,
  COLOR_CONFLICT_FLAG,
  SEVERITY_OPTIONS,
} from "@constants/index";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { getIndexDBData, STORES } from "@utils/indexedDb";
import { displayValueInRange, formatDate, getBackgroundColor, formatVerificationType } from "@utils/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import useVulnerabilityPolicy from "@hooks/useVulnerabilityPolicy";
import { useMutationData } from "@hooks/useMutationData";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";
import queryKeys from "@configs/react-query/query-key";
import { useQueries } from "@tanstack/react-query";
import { fetchVexProgress } from "@apis/vex";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { resolveVexScanToolCd, vexComponentKey, vexContextKey } from "@constants/vex";
import {
  getComponentLicenseConflict,
  getLicensePolicyResult,
  capitalizeFirstLetter,
} from "../../../utils/common";

export default function VulnerabilityStatus() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { fetchSearchInfo, verifyTypeList, scanToolList } = useSearchInfo();

    const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [searchFilters, setSearchFilters] = useState([]);
  const [vulnerabilityStatusList, setVulnerabilityStatusList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.VULNERABILITY_STATUS, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [securityModalData, setSecurityModalData] = useState(null);
  const [securityModalVexContext, setSecurityModalVexContext] = useState(null);
  const [securityModalComponent, setSecurityModalComponent] = useState(null);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [isVulnerabilityStatusLoading, setIsVulnerabilityStatusLoading] = useState(false);
  const isInitialMount = useRef(true);
  const prevSearchFilters = useRef({});
  const prevCurrentPage = useRef(1);
  const { vulnerabilityLevel } = useVulnerabilityPolicy();
  const [isLicenseConflictModalOpen, setIsLicenseConflictModalOpen] = useState(false);
  const [isPolicyConflictModalOpen, setIsPolicyConflictModalOpen] = useState(false);
  const [selectedConflictData, setSelectedConflictData] = useState([]);
  const [selectedLicensePolicyResult, setSelectedLicensePolicyResult] = useState(null);
  const [loadingPolicyRowKey, setLoadingPolicyRowKey] = useState(null);
  const [loadingLicenseRowKey, setLoadingLicenseRowKey] = useState(null);
  const [selectedComponentData, setSelectedComponentData] = useState(null);
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false);

  const clientConfig = useClientConfig();
  const isVexOn = Boolean(clientConfig?.isClientConfigEnabled?.("useVex"));

  const allowScanToolKeys = useMemo(
    () => (scanToolList ?? []).map((t) => t.value.toLowerCase()),
    [scanToolList]
  );

  /** Each row is one component of its own verification — VEX progress is scoped per row */
  const resolveRowVexScope = useCallback((row) => {
    const scanToolCd = resolveVexScanToolCd(row?.verificationTypeCd);
    const componentName = row?.component || row?.componentName || "";
    const contextKey = vexContextKey({ scanToolCd, verificationSeq: row?.verificationSeq });
    if (!contextKey || !componentName) return null;
    const componentVersion = row?.componentVersion || "";
    return {
      key: `${contextKey}::${vexComponentKey(componentName, componentVersion)}`,
      scanToolCd,
      verificationSeq: row.verificationSeq,
      componentName,
      componentVersion,
    };
  }, []);

  const vexProgressScopes = useMemo(() => {
    if (!isVexOn) return [];
    const byKey = new Map();
    (vulnerabilityStatusList || []).forEach((row) => {
      const hasSecurities = row?.securities != null && row.securities !== "[]" && row.securities !== "";
      if (!hasSecurities) return;
      const scope = resolveRowVexScope(row);
      if (scope && !byKey.has(scope.key)) byKey.set(scope.key, scope);
    });
    return Array.from(byKey.values());
  }, [isVexOn, vulnerabilityStatusList, resolveRowVexScope]);

  /** Component-scoped GET /vex/progress — lighter than full /vex list */
  const vexProgressQueries = useQueries({
    queries: vexProgressScopes.map(({ scanToolCd, verificationSeq, componentName, componentVersion }) => ({
      queryKey: [queryKeys.vexProgress, scanToolCd, verificationSeq, componentName, componentVersion],
      queryFn: () =>
        fetchVexProgress({ scanToolCd, verificationSeq, componentName, componentVersion }),
      enabled: isVexOn,
    })),
  });

  const vexProgressByScope = useMemo(() => {
    const map = new Map();
    vexProgressScopes.forEach((scope, index) => {
      const progress = vexProgressQueries[index]?.data;
      if (progress && progress.total != null) map.set(scope.key, progress);
    });
    return map;
  }, [vexProgressScopes, vexProgressQueries]);

  const getRowVexProgress = (row) => {
    const scope = resolveRowVexScope(row);
    return scope ? vexProgressByScope.get(scope.key) || null : null;
  };

  const filteredVerifyTypeList = useMemo(() => {
    return verifyTypeList.filter((item) =>
      allowScanToolKeys.includes(item.value.toLowerCase())
    );
  }, [allowScanToolKeys, verifyTypeList])

  const getListData = async (bodyRequest = {}) => {
    setIsVulnerabilityStatusLoading(true);
    try {
      const { pageSize = itemsPerPage, startRow = 0, ...filters } = bodyRequest;
      const response = await getIndexDBData(STORES.VULNERABILITY_STATUSES, {
        pageSize,
        startRow,
        filters,
        sortKey: "vulnerabilityLevelMark,projectCreatedAt",
        sortOrder: "asc,desc",
      });

      if (response && typeof response !== "string") {
        setVulnerabilityStatusList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching vulnerability status from IndexedDB:", error);
      showErrorToast(t("preferences.loading-data-error"));
    } finally {
      setIsVulnerabilityStatusLoading(false);
    }
  };

  const fetchData = useCallback(() => {
    if (isVulnerabilityStatusLoading) {
      return;
    }

    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined && value !== null) {
        // Format date fields using formatDate function
        if (key === "createdAtFrom" || key === "createdAtTo" &&
          value instanceof Date) {
          acc[key] = formatDate(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData(bodyRequest);
  }, [searchFilters, currentPage, itemsPerPage, isVulnerabilityStatusLoading]);

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  useEffect(() => {
    fetchSearchInfo();
    fetchData();
  }, []);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  useEffect(() => {
    if (!isInitialMount.current) {
      const currentFilters = JSON.stringify(searchFilters);
      const prevFilters = JSON.stringify(prevSearchFilters.current);

      if (currentFilters !== prevFilters) {
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          fetchData();
        }
        prevSearchFilters.current = searchFilters;
      }
    } else {
      isInitialMount.current = false;
      prevSearchFilters.current = searchFilters;
      prevCurrentPage.current = currentPage;
    }
  }, [searchFilters]);

  // Call API when itemsPerPage changes
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [itemsPerPage]);

  const getLicenseAttribute = useMutationData(
    (licenseIdentifier) => `${queryKeys.licenseAttributeByLicenseIdentifier}?licenseIdentifier=${licenseIdentifier}`,
    { method: "GET" },
    {
      onSuccess: () => { },
      onError: (error) => {
        console.error("Error during get data:", error);
        showErrorToast(t("common.get-license-attribute-error"));
      },
    },
  );

  const getLicensePolicy = useMutationData(
    (licensePolicySeq) => `license_policy/${licensePolicySeq}`,
    { method: "GET" },
    {
      onSuccess: () => { },
      onError: (error) => {
        console.error("Error during get data:", error);
        showErrorToast(t("common.get-license-attribute-error"));
      },
    },
  );

  const getBinaryScanComponent = useMutationData(
    ({ projectId, verificationVersion }) =>
      `/binary_scan_component?projectId=${projectId}&verificationVersion=${verificationVersion}`,
    { method: "GET" },
    {
      onSuccess: (data) => { },
      onError: (error) => {
        console.error("Error fetching binary scan component:", error);
        showErrorToast("Error fetching binary scan component");
      },
    }
  );

  const getSourceCodeScanComponent = useMutationData(
    ({ projectId, verificationVersion }) =>
      `/source_code_scan_component?projectId=${projectId}&verificationVersion=${verificationVersion}`,
    { method: "GET" },
    {
      onSuccess: (data) => { },
      onError: (error) => {
        console.error("Error fetching source code scan component:", error);
        showErrorToast("Error fetching source code scan component");
      },
    }
  );

  const getSbomScanComponent = useMutationData(
    ({ projectId, verificationVersion }) =>
      `/sbom_scan_component?projectId=${projectId}&verificationVersion=${verificationVersion}`,
    { method: "GET" },
    {
      onSuccess: (data) => { },
      onError: (error) => {
        console.error("Error fetching sbom scan component:", error);
        showErrorToast("Error fetching SBOM scan component");
      },
    }
  );

  const columns = [
    "vulnerabilityLevel",
    "doneObligationType",
    "conflictStatus",
    "projectName",
    "verificationFileName",
    "verificationTypeCd",
    "verificationVersion",
    "componentInformation",
    "severity",
    "security",
  ];

  const textCellStyle = {
    whiteSpace: "nowrap",
    overflowWrap: "initial",
  };

  const columnConfig = {
    vulnerabilityLevel: {
      title: t("vulnerability-policy.vulnerability-security-code"),
      truncate: false,
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
      },
      headerRender: () => t("vulnerability-policy.vulnerability-security-code"),
      customRender: (_, row) => {
        const color = vulnerabilityLevel.find(v => v.levelCode === row.vulnerabilityLevel)?.vulnerabilityLevel || "gray";
        return (
          <div className="flex items-center justify-center w-full">
            <div
              className={`w-8 h-8 flex items-center justify-center text-base font-bold rounded-sm ${colors.textStaticWhite}`}
              style={{
                backgroundColor: VULNERABILITY_CODE_COLOR[color],
                color: "white",
              }}
            >
              <span>{row.vulnerabilityLevel}</span>
            </div>
          </div>
        );
      },
    },
    doneObligationType: {
      truncate: false,
      style: {
        width: "15%",
        whiteSpace: "normal",
        textAlign: "center",
      },
      headerRender: () => t("status.obligation-type"),
      customRender: (value, row) => (
        <div className="flex items-center justify-center w-full">
          <div
            className="w-8 h-8 flex items-center justify-center text-base font-bold rounded-sm"
            style={{
              backgroundColor: getBackgroundColor(row.doneObligationTypeColor),
              color: getBackgroundColor(row.doneObligationTypeColor) === "#FFFFFF" ? "#000000" : "#FFFFFF",
              border: getBackgroundColor(row.doneObligationTypeColor) === "#FFFFFF" ? "1px solid #9CA3AF" : "none",
              pointerEvents: "none",
            }}
          >
            {displayValueInRange(row.doneObligationType)}
          </div>
        </div>
      ),
    },
    conflictStatus: {
      truncate: false,
      style: {
        width: "15%",
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
      },
      headerRender: () => t("common.conflict"),
      customRender: (value, row) => {
        const rowKey = `${row.projectId ?? ""}_${row.verificationVersion ?? ""}_${row.component ?? ""}_${row.componentLicense ?? ""}_${uuidv4()}`;
        const isPolicyConflict = row.donePolicyConflictFlag?.toUpperCase() === "Y";
        const isLicenseConflict = row.doneLicenseConflictBetweenComponentsFlag?.toUpperCase() === "Y";
        const isPolicyLoading = loadingPolicyRowKey === rowKey;
        const isLicenseLoading = loadingLicenseRowKey === rowKey;

        const handlePolicyClick = async () => {
          if (!isPolicyConflict || isPolicyLoading) return;
          setLoadingPolicyRowKey(rowKey);
          try {
            const licenseAttribute = await getLicenseAttribute.mutateAsync(row.componentLicense);
            const licensePolicy = await getLicensePolicy.mutateAsync(row.licensePolicySeq);
            const licensePolicyResult = getLicensePolicyResult(licensePolicy, licenseAttribute);

            setSelectedLicensePolicyResult(licensePolicyResult);
            setIsPolicyConflictModalOpen(true);
          } finally {
            setLoadingPolicyRowKey(null);
          }
        };

        const handleLicenseClick = async () => {
          if (!isLicenseConflict || isLicenseLoading) return;
          setLoadingLicenseRowKey(rowKey);

          try {
            const rawAttr = await getLicenseAttribute.mutateAsync(row.componentLicense);
            const licenseAttribute = {
              licenseAttribute: rawAttr,
              component: row.component,
              componentLicense: row.componentLicense,
            };
            const fetcherMap = {
              clarity: getBinaryScanComponent,
              fossid: getSourceCodeScanComponent,
              sbom: getSbomScanComponent,
            };
            const fetcher = fetcherMap[row.verificationTypeCd];
            if (!fetcher) return;
            const scanComponent = await fetcher.mutateAsync({
              projectId: row.projectId,
              verificationVersion: row.verificationVersion,
            });
            const conflictData = getComponentLicenseConflict(scanComponent, licenseAttribute);
            setSelectedConflictData(conflictData?.conflictComponents ?? []);
            setIsLicenseConflictModalOpen(true);
          } finally {
            setLoadingLicenseRowKey(null);
          }
        };

        return (
          <div className="flex items-center justify-center w-full">
            <div className="flex items-center gap-x-0.5">
              <div
                className={`w-8 h-8 flex items-center justify-center text-base font-bold rounded-l-sm 
                  ${(isPolicyConflict && !isPolicyLoading) ? "cursor-pointer hover:opacity-80" : "cursor-not-allowed opacity-50"}`}
                style={{
                  backgroundColor: isPolicyConflict
                    ? COLOR_CONFLICT_FLAG.POLICY_CONFLICT
                    : COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT,
                  borderRight: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isPolicyLoading) return;
                  handlePolicyClick();
                }}
              >
                <span className={isPolicyConflict ? colors.textStaticWhite : colors.textConflictUndefined}>P</span>
              </div>
              <div
                className={`w-8 h-8 flex items-center justify-center text-base font-bold rounded-r-sm 
                  ${(isLicenseConflict && !isLicenseLoading) ? "cursor-pointer hover:opacity-80" : "cursor-not-allowed opacity-50"}`}
                style={{
                  backgroundColor: isLicenseConflict
                    ? COLOR_CONFLICT_FLAG.LICENSE_CONFLICT
                    : COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT,
                  borderLeft: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isLicenseLoading) return;
                  handleLicenseClick();
                }}
              >
                <span className={isLicenseConflict ? colors.textStaticWhite : colors.textConflictUndefined}>L</span>
              </div>
            </div>
          </div>
        );
      },
    },
    projectName: {
      style: {
        width: "15%",
        ...textCellStyle,
      },
      headerRender: () => t("status.project-name"),
    },
    verificationFileName: {
      style: {
        width: "15%",
        ...textCellStyle,
      },
      headerRender: () => t("status.verif-file-name"),
    },
    verificationTypeCd: {
      style: {
        width: "10%",
        ...textCellStyle,
      },
      headerRender: () => t("status.verification-type"),
      customRender: (value) => {
        const formattedValue = formatVerificationType(value);
        return formattedValue;
      },
    },
    verificationVersion: {
      style: {
        width: "10%",
        ...textCellStyle,
      },
      headerRender: () => t("common.version"),
    },
    componentInformation: {
      truncate: false,
      style: {
        width: "24%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => <ComponentHeader />,
      customRender: (value, row) => {
        const componentVersion = row.componentVersion || "";
        const licenseIdentifier = row.componentLicense || row.licenseIdentifier || row.licenseName;
        const isValidLicense = licenseIdentifier != null && licenseIdentifier.toUpperCase() !== "N/A";
        const licenseText = isValidLicense ? (licenseIdentifier || "") : "";

        const componentName = row.component || row.componentName || "";
        const componentLine = componentVersion
          ? `${componentName} (${componentVersion})`
          : componentName;

        const handleLicenseClick = (e) => {
          e.stopPropagation();
          if (row.componentLicense) {
            setSelectedLicense(row.componentLicense);
            setShowLicenseModal(true);
          }
        };
        const handleComponentClick = (e) => {
          e.stopPropagation();
          setSelectedComponentData(row);
          setIsComponentModalOpen(true);
        };

        return (
          <div className="min-w-0 w-full">
            <div
              className={`truncate text-sm font-medium cursor-pointer transition-colors ${colors.textNeutralLight} ${colors.hoverTextAccentCyan}`}
              title={componentLine}
              onClick={handleComponentClick}
            >
              <span>{componentName}</span>
              {componentVersion && (
                <span className="text-gray-400">{` (${componentVersion})`}</span>
              )}
            </div>
            <div
              className={`mt-0.5 truncate text-xs ${isValidLicense ? `cursor-pointer ${colors.textActive} ${colors.hoverTextComparisonLink} transition-colors` : colors.textNeutralMuted}`}
              title={licenseText || undefined}
              onClick={isValidLicense ? handleLicenseClick : undefined}
            >
              <span className={licenseText ? "" : "invisible"}>{licenseText || "-"}</span>
            </div>
          </div>
        );
      },
    },
    severity: {
      style: {
        width: "10%",
        ...textCellStyle,
      },
      headerRender: () => t("status.severity"),
      customRender: (value, row) => {
        const severityText = capitalizeFirstLetter(row.cvssScore) || "-";
        return (
          <span className="block truncate" title={severityText}>
            {severityText}
          </span>
        );
      },
    },
    security: {
      truncate: false,
      style: {
        width: "10%",
        whiteSpace: "normal",
        overflowWrap: "initial",
        textAlign: "center",
      },
      headerRender: () => t("status.securities"),
      customRender: (value, row) => {
        const handleSecurityClick = (e) => {
          e.stopPropagation();

          let parsedValue = null;
          try {
            if (typeof row.securities === "string" && row.securities.trim() !== "") {
              parsedValue = JSON.parse(row.securities);
            } else if (
              Array.isArray(row.securities) ||
              (typeof row.securities === "object" && row.securities !== null)
            ) {
              parsedValue = row.securities;
            }
          } catch (e) {
            console.error("Error parsing securities data:", e);
          }

          if (parsedValue && Array.isArray(parsedValue) && parsedValue.length > 0) {
            setSecurityModalData(parsedValue);
            setSecurityModalVexContext({
              scanToolCd: row.verificationTypeCd,
              verificationSeq: row.verificationSeq,
              componentName: row.component || row.componentName || "",
              componentVersion: row.componentVersion || "",
            });
            setSecurityModalComponent({
              componentName: row.component || row.componentName || "",
              componentVersion: row.componentVersion || "",
            });
            setShowSecurityModal(true);
          }
        };

        return (
          <div className="flex flex-col items-center justify-center gap-0.5 min-w-0">
            {row.securities !== null && row.securities !== "[]" && row.securities !== "" && (
              <>
                <ShieldCheckIcon
                  size={16}
                  onClick={handleSecurityClick}
                  className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                />
                {isVexOn && (
                  <VexSecuritiesBadge
                    progress={getRowVexProgress(row)}
                    onClick={handleSecurityClick}
                    data-testid={`vulnerability-status-securities-${row.projectId ?? ""}-${row.verificationVersion ?? ""}-${row.component ?? ""}-vex-badge`}
                  />
                )}
              </>
            )}
          </div>
        );
      },
    },
  };

  const searchFields = [
    {
      name: "verificationTypeCd",
      label: t("status.verification-type"),
      type: "select",
      keepRawLabel: true,
      placeholder: t("project.verification-type-placeholder"),
      options: [...filteredVerifyTypeList],
    },
    {
      name: "verificationVersion",
      label: t("status.verification-version"),
      placeholder: t("status.verification-version-placeholder"),
    },
    {
      name: "projectName",
      label: t("status.project-name"),
      placeholder: t("status.project-name-placeholder"),
    },
    {
      name: "projectId",
      label: t("status.project-id"),
      placeholder: t("status.project-id-placeholder"),
    },
    {
      name: "donePolicyConflictFlag",
      label: t("status.policy-conflict"),
      type: "select",
      placeholder: t("status.policy-conflict-placeholder"),
      options: [...FLAG_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))],
    },
    {
      name: "doneLicenseConflictBetweenComponentsFlag",
      label: t("status.license-conflict"),
      type: "select",
      placeholder: t("status.license-conflict-placeholder"),
      options: [...FLAG_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))],
    },
    {
      name: "component",
      label: t("status.component"),
      placeholder: t("status.component-placeholder"),
    },
    {
      name: "componentVersion",
      label: t("status.component-version"),
      placeholder: t("status.component-version-placeholder"),
    },
    {
      name: "componentLicense",
      label: t("status.component-license"),
      placeholder: t("status.component-license-placeholder"),
    },
    {
      name: "cvssScore",
      label: t("status.severity"),
      type: "select",
      options: [...SEVERITY_OPTIONS],
      placeholder: t("status.severity-placeholder"),
    },
    {
      name: "doneObligationType",
      label: t("status.obligation-type"),
      type: "select",
      placeholder: t("status.obligation-type-placeholder"),
      options: [...OBLIGATION_TYPE_OPTIONS],
    },
    {
      name: "vulnerabilityLevel",
      label: t("status.vulnerability-level"),
      type: "select",
      placeholder: t("status.vulnerability-security-code-placeholder"),
      options: [...VUlNERABILITY_TYPE_OPTIONS],
    },
    {
      name: "cve",
      label: t("status.cve"),
      placeholder: t("status.cve"),
    },
    {
      name: "createdAtFrom",
      label: "Creation Date (From)",
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: "Creation Date (To)",
      placeholder: t("common.create-at-to"),
      type: "date",
      minDate: "createdAtFrom", // Prevent selecting date before createdAtFrom
    },
    {
      name: "cvss",
      label: t("project.cvss-version"),
      placeholder: t("project.cvss-version"),
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const onRowClick = (row) => {
    const routes = {
      clarity: `../projects/${row.projectSeq}/clarity/${row.verificationSeq}`,
      fossid: `../projects/${row.projectSeq}/fossid/${row.verificationSeq}`,
      sbom: `../projects/${row.projectSeq}/sbom/${row.verificationSeq}`,
      clarityair: `../projects/${row.projectSeq}/clarity-air/${row.verificationSeq}`,
    };

    if (routes[row.verificationTypeCd]) {
      navigate(routes[row.verificationTypeCd]);
    }
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="vulnerability-status-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`}>
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>
            {t("status.vulnerability-status-information-list")}
          </h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 pr-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          placeholderSearch={t("common.search-placeholder")}
          isDisplayAddButton={false}
          isDisplayDeleteButton={false}
          searchInputTestId="vulnerability-status-search-input"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div data-testid="vulnerability-status-advanced-search">
          <AdvancedSearch
            title={t("common.advanced-search")}
            searchFields={[...searchFields]}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      <div className="flex-grow min-h-0" data-testid="vulnerability-status-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={vulnerabilityStatusList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isVulnerabilityStatusLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          disabledRow={[]}
          shouldShowGoToPage={true}
          showItemsPerPageSelector={true}
        />
      </div>

      {/* Security Information Modal */}
      <SecurityInformationModal
        show={showSecurityModal}
        onClose={() => {
          setShowSecurityModal(false);
          setSecurityModalData(null);
          setSecurityModalVexContext(null);
          setSecurityModalComponent(null);
        }}
        data={securityModalData}
        readOnlyVexReview
        scanToolCd={securityModalVexContext?.scanToolCd}
        verificationSeq={securityModalVexContext?.verificationSeq}
        componentName={securityModalVexContext?.componentName}
        componentVersion={securityModalVexContext?.componentVersion}
      />

      {/* License Information Modal */}
      <LicenseInformationModal
        show={showLicenseModal}
        onClose={() => {
          setShowLicenseModal(false);
          setSelectedLicense(null);
        }}
        componentLicense={selectedLicense}
      />
      <LicenseConflictModal
        isOpen={isLicenseConflictModalOpen}
        onClose={() => {
          setIsLicenseConflictModalOpen(false);
          setSelectedConflictData([]);
        }}
        conflictData={selectedConflictData}
      />
      <PolicyConflictModal
        isOpen={isPolicyConflictModalOpen}
        onClose={() => {
          setIsPolicyConflictModalOpen(false);
          setSelectedLicensePolicyResult(null);
        }}
        licensePolicyResult={selectedLicensePolicyResult}
      />
      <ComponentInformationModal
        show={isComponentModalOpen}
        onClose={() => {
          setIsComponentModalOpen(false);
          setSelectedComponentData(null);
        }}
        componentData={selectedComponentData}
      />
    </div>
  );
}
